import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import Lozenge from '@atlaskit/lozenge';
import { N0, N900 } from '@atlaskit/theme/colors';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { FormattedPriceCalculationResponse } from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { TRACKING_EVENT_NAMES, UNIT_TRACKING_TERMS } from '../utils/constants';

export type UserTierProps = {
  readonly unitTrackingTerm?: UNIT_TRACKING_TERMS;
  readonly priceCalc: FormattedPriceCalculationResponse;
};

export const UserTier = ({
  unitTrackingTerm = UNIT_TRACKING_TERMS.users,
  priceCalc
}: UserTierProps) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();

  const onMouseOver = () => {
    if (createAnalyticsEvent) {
      const analyticsEvent = createAnalyticsEvent({
        name: TRACKING_EVENT_NAMES.userTierTooltipTriggered,
        label: unitTrackingTerm
      });
      analyticsEvent.fire();
    }
  };

  const { formattedBreakdown } = priceCalc;

  const fmtBreakdownTier = formattedBreakdown[formattedBreakdown.length - 1];
  if (!fmtBreakdownTier) {
    throw new Error('No breakdown provided for user tier.');
  }

  const {
    formattedUnitLimitLower,
    formattedUnitLimitUpper,
    formattedUnitsInTier,
    formattedUnitsAvailableInTier
  } = fmtBreakdownTier;

  return (
    <UserTierContainer>
      <Tooltip
        delay={1000}
        component={TooltipContentContainer}
        content={
          <>
            <UserTierDetailsContainer>
              <FormattedMessage
                id="userTierDetails"
                defaultMessage="Details for this tier"
                description="userTierDetails label"
              />
            </UserTierDetailsContainer>
            <MarginContainer></MarginContainer>
            <ToolTipLabelContainer>
              <FormattedMessage
                id="tier"
                defaultMessage="tier"
                description="tier label"
              />
            </ToolTipLabelContainer>
            <LimitsContainer>
              <FormattedMessage
                id="limits"
                defaultMessage="{formattedUnitLimitLower} - {formattedUnitLimitUpper}"
                description="limits"
                values={{
                  formattedUnitLimitLower,
                  formattedUnitLimitUpper
                }}
              />
            </LimitsContainer>

            <ToolTipLabelContainer>
              <FormattedMessage
                id="filledSeats"
                defaultMessage="Filled seats"
                description="filledSeats label"
              />
            </ToolTipLabelContainer>
            <LimitsContainer>{formattedUnitsInTier}</LimitsContainer>
            <ToolTipLabelContainer>
              <FormattedMessage
                id="availableSeats"
                defaultMessage="Available seats"
                description="availableSeats label"
              />
            </ToolTipLabelContainer>
            <LimitsContainer>{formattedUnitsAvailableInTier}</LimitsContainer>
          </>
        }
        position={'bottom-end'}
      >
        <TooltipContainer onMouseOver={() => onMouseOver()}>
          <Lozenge style={{ backgroundColor: '#172B4D', color: N0 }}>
            <UserTierMessage>
              <FormattedMessage
                id="userTier"
                defaultMessage="User tier: {formattedUnitLimitLower} - {formattedUnitLimitUpper}"
                description="User tier"
                values={{
                  formattedUnitLimitLower,
                  formattedUnitLimitUpper
                }}
              />
            </UserTierMessage>
          </Lozenge>
          <QuestionIconContainer>
            <QuestionCircleIcon size="medium" label="?" primaryColor="1E3859" />
          </QuestionIconContainer>
        </TooltipContainer>
      </Tooltip>
    </UserTierContainer>
  );
};

const UserTierContainer = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
`;

const UserTierMessage = styled.div`
  border-radius: 4px;
  font-family: 'Charlie Text';
  font-size: 11px;
  font-style: normal;
  font-weight: normal;
  line-height: 16px;
  text-transform: uppercase;
`;

const QuestionIconContainer = styled.div`
  align-items: center;
  display: flex;
  margin-left: 12px;
`;

const TooltipContainer = styled.div`
  align-items: center;
  display: flex;

  svg {
    font-weight: normal;
  }
`;

const TooltipContentContainer = styled(TooltipPrimitive)`
  background: ${N900};
  border-radius: 12px;
  box-shadow: 0px 3px 5px rgba(9, 30, 66, 0.2),
    0px 0px 1px rgba(9, 30, 66, 0.31);
  max-width: 240px;
  padding: 3px 3px 3px 1px;
`;

const UserTierDetailsContainer = styled.div`
  color: ${N0};
  font-family: Charlie Display;
  font-size: 20px;
  letter-spacing: 0.3px;
  line-height: 24px;
  margin-bottom: 20px;
  margin-left: 40px;
  margin-top: 20px;
  padding-right: 32px;
`;

const MarginContainer = styled.div`
  background: ${N0};
  height: 1px;
  margin-bottom: 20px;
  margin-left: 30px;
  width: 176px;
`;

const ToolTipLabelContainer = styled.div`
  color: ${N0};
  font-family: Charlie Display;
  font-size: 12px;
  height: 21px;
  letter-spacing: 1px;
  line-height: 21px;
  margin-left: 30px;
  text-transform: uppercase;
`;

const LimitsContainer = styled.div`
  color: ${N0};
  font-family: Charlie Text;
  font-size: 16px;
  height: 24px;
  line-height: 24px;
  margin-bottom: 20px;
  margin-left: 30px;
  text-transform: uppercase;
`;
